'use client';
import Bugsnag, { type Event } from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import { isUndefined } from 'lodash';
import LogRocket from 'logrocket';
import React, { Fragment } from 'react';

import { isTurbopackChunkLoadNoise } from './isTurbopackChunkLoadNoise';

const RECAPTCHA_IGNORED_MESSAGES = [
  'reCAPTCHA Timeout',
  'Cannot read properties of undefined',
  'Cannot convert undefined or null to object',
  'Missing required parameters: sitekey',
  'reCAPTCHA has already been rendered in this element',
];

// Drop known-noise reCAPTCHA errors. These don't affect the app (verified via
// LogRocket) and can't be "Ignored" in the Bugsnag UI because Google rotates the
// bundle hash — each rotation produces a new stack signature and a new error group.
// Explicit allowlist: an error is dropped only if its message matches one of these
// AND at least one stack frame is in gstatic.com/recaptcha or @google-recaptcha.
//
// Patterns use stable substrings only — no minified property names (e.g. 'sf',
// 'hpm') since Google rotates those every bundle. "Cannot read properties of
// undefined" without a tail covers the whole obfuscated-property family.
const isKnownRecaptchaNoise = (event: Event) =>
  event.errors.some(
    (err) =>
      RECAPTCHA_IGNORED_MESSAGES.some((needle) => err.errorMessage?.includes(needle)) &&
      err.stacktrace?.some(
        (frame) => frame.file?.includes('gstatic.com/recaptcha') || frame.file?.includes('@google-recaptcha'),
      ),
  );

export const initializeBugsnag = ({
  releaseStage,
  appVersion,
  apiKey,
}: {
  apiKey: string;
  appVersion: string;
  releaseStage: string;
}) => {
  /**
   * This if prevents Bugnsnag from possibly running more than once.
   * This usually happens while in the dev environment.
   * @see https://github.com/bugsnag/bugsnag-js/issues/811#issuecomment-703853628
   */
  // @ts-expect-error - Bugsnag._client is not a documented property
  if (!Bugsnag._client) {
    if (apiKey) {
      Bugsnag.start({
        apiKey,
        appVersion,
        logger: null,
        onError: (event) => {
          // events to ignore
          if (event.originalError === 'ResizeObserver loop limit exceeded') {
            return false;
          }

          if (isKnownRecaptchaNoise(event)) {
            return false;
          }

          if (isTurbopackChunkLoadNoise(event)) {
            return false;
          }

          if (typeof window !== 'undefined') {
            /**
             * Disable Bugsnag in localhost
             */
            if (window.location.hostname === 'localhost') {
              return false;
            }

            if ('hasLogRocketInitialized' in window && window.hasLogRocketInitialized) {
              event.addMetadata('LogRocket', {
                sessionURL: LogRocket.sessionURL,
              });
            }

            const isInIframe = window.top !== window.self;

            if (isInIframe) {
              event.addMetadata('isInIframe', {
                isInIframe,
                // document.location.ancestorOrigins is not supported on Firefox, I couldn't find a replacement
                parentLocation: document.location.ancestorOrigins ? document.location.ancestorOrigins[0] : 'Unknown',
              });
            }
          }
        },
        plugins: [new BugsnagPluginReact(React)],
        releaseStage,
      });
    } else {
      console.error('Bugsnag API key not found. Please set NEXT_PUBLIC_BUGSNAG in your .env file.');
    }
  }
};

const BugsnagReactPlugin = Bugsnag.getPlugin('react');

export const ErrorBoundary = isUndefined(BugsnagReactPlugin) ? Fragment : BugsnagReactPlugin.createErrorBoundary(React);

export { Bugsnag };
